import type { AxiosResponse } from 'axios'
import axios from 'axios'
import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import type { GenericQueryOptions } from '.'
import type { BaseAddressDto } from '../dtos'
import type { Item, SimpleItem } from '../dtos/Selection'
import type { Risk, SelectionType } from '../enums'

export const longStaleTime: number = 1000 * 60 * 60 * 24 // 1 den
export const oneHourStaleTime: number = 1000 * 60 * 60 // 1 hodina
const baseUrl = import.meta.env.VITE_BASE_URL
const apiUrl = import.meta.env.VITE_ENUM_API
const url = `${baseUrl}/${apiUrl}`

export type Enums = 'selection' | 'insurer' | 'country' | 'region' | 'risk' | 'riskVariant'

const fetchSelections = (selectionType?: SelectionType) =>
    axios.get<Item[]>(`${url}/GetSelections?type=${selectionType}`)

const fetchInsurers = (addonOnly: boolean | null) =>
    axios.get<Item[]>(`${url}/GetInsurers?addonProviders=${addonOnly}`)

const fetchCountries = () =>
    axios.get<SimpleItem[]>(`${url}/GetCountries`)

const fetchRegions = (postCode: string) =>
    axios.get<BaseAddressDto[]>(`${url}/GetRegions?postCode=${postCode}`)

const fetchYearKm = (categoryId: number) =>
    axios.get<SimpleItem[]>(`${url}/GetYearKm?categoryId=${categoryId}`)

export const useSelections = (selectionType?: SelectionType, options?: GenericQueryOptions<Item[]>): UseQueryResult<AxiosResponse<Item[], any>, any> =>
    useQuery({ queryKey: ['selection', selectionType], queryFn: () => fetchSelections(selectionType), staleTime: longStaleTime, ...options })

export const useInsurers = (addonOnly: boolean | null, options?: GenericQueryOptions<Item[]>): UseQueryResult<AxiosResponse<Item[], any>, any> =>
    useQuery({ queryKey: ['insurer', addonOnly], queryFn: () => fetchInsurers(addonOnly), staleTime: longStaleTime, ...options })

export const useCountries = (enabled?: boolean, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> =>
    useQuery({ queryKey: ['country'], queryFn: () => fetchCountries(), staleTime: longStaleTime, enabled, ...options })

export const useRegions = (postCode: string, options?: GenericQueryOptions<BaseAddressDto[]>): UseQueryResult<AxiosResponse<BaseAddressDto[], any>, any> =>
    useQuery({ queryKey: ['region', postCode], queryFn: () => fetchRegions(postCode), staleTime: longStaleTime, ...options })

export const useGetRisk = (options?: GenericQueryOptions<Item[]>): UseQueryResult<AxiosResponse<Item[], any>, any> =>
    useQuery({ queryKey: ['risk'], queryFn: () => axios.get<Item[]>(`${url}/GetRisks`), staleTime: longStaleTime, ...options })

export const useGetRiskVariants = (risk?: Risk, options?: GenericQueryOptions<Item[]>): UseQueryResult<AxiosResponse<Item[], any>, any> =>
    useQuery({ queryKey: ['riskVariant', risk], queryFn: () => axios.get<Item[]>(`${url}/GetRiskVariants?riskId=${risk}`), staleTime: longStaleTime, ...options })

export const useYearKm = (categoryId: number, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<AxiosResponse<SimpleItem[], any>, any> =>
    useQuery({ queryKey: ['useYearKm', categoryId], queryFn: () => fetchYearKm(categoryId), staleTime: longStaleTime, ...options })